import { Component, ElementRef, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { Select, Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { MatSelect } from '@angular/material/select';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Organization } from 'app/models/organization.model';
import { OrganizationsState } from 'app/state-management/states/organizations.states';
import { SetOrganization } from 'app/state-management/actions/organizations.actions';

// страницы меню: их компоненты загружают данные один раз в ngOnInit,
// поэтому при смене организации их нужно пересоздавать
const MENU_PAGES = ['menu', 'menu-edit', 'menu-events'];

@UntilDestroy()
@Component({
  selector: 'app-organization-select',
  templateUrl: './organization-select.component.html',
  styleUrls: ['./organization-select.component.scss'],
  // стили панели рендерятся в overlay вне компонента
  encapsulation: ViewEncapsulation.None,
})
export class OrganizationSelectComponent implements OnInit {
  @Select(OrganizationsState.getActiveBranches) organizations$: Observable<Organization[]>;
  @Select(OrganizationsState.getSelectedOrganizationId) selectedOrganizationId$: Observable<string>;
  @ViewChild('organizationSelect') organizationSelect: MatSelect;
  @ViewChild('searchInput') searchInput: ElementRef<HTMLInputElement>;

  organizations: Organization[] = [];
  filteredOrganizations: Organization[] = [];
  filterValue = '';
  selectedOrganizationId: string;

  constructor(
    private router: Router,
    private store: Store,
  ) {
  }

  ngOnInit(): void {
    this.organizations$
      .pipe(
        untilDestroyed(this)
      )
      .subscribe(organizations => {
        this.organizations = organizations;
        this.filteredOrganizations = this.getFilteredOrganizations();
      });
    this.selectedOrganizationId$
      .pipe(
        untilDestroyed(this)
      )
      .subscribe(organizationId => {
        this.selectedOrganizationId = organizationId;
      });
  }

  onOrganizationChange(): void {
    const urlTree = this.router.parseUrl(this.router.url);
    const segments = urlTree.root.children['primary']?.segments.map(s => s.path) ?? [];
    // меняем только организацию в адресе, если текущий URL вида /app/:orgId/...
    const isOrganizationUrl = segments[0] === 'app' && this.organizations.some(o => o.id === segments[1]);
    if (!isOrganizationUrl) {
      // страница не содержит организацию в адресе — остаёмся на ней,
      // только фиксируем выбор в сторе (как это делает резолвер при навигации)
      this.store.dispatch(new SetOrganization(this.selectedOrganizationId));
      return;
    }
    const rest = this.stripOldOrganizationMenuSection(segments.slice(2));
    const target = ['/app', this.selectedOrganizationId, ...rest];
    if (MENU_PAGES.includes(rest[0])) {
      // компоненты меню при смене организации переиспользуются роутером
      // со старыми данными — пересоздаём их через промежуточную навигацию,
      // чтобы всё загрузилось с бэка заново
      this.router.navigateByUrl('/app/notfound', { skipLocationChange: true }).then(() =>
        this.router.navigate(target, { queryParams: urlTree.queryParams })
      );
      return;
    }
    this.router.navigate(target, { queryParams: urlTree.queryParams });
  }

  // id секции меню принадлежит старой организации — убираем его,
  // резолвер меню подставит секцию новой организации
  private stripOldOrganizationMenuSection(rest: string[]): string[] {
    if (rest[0] === 'menu' && rest[1] === 'main' && rest.length > 2) {
      return ['menu', 'main'];
    }
    return rest;
  }

  filterValueChange(value: string): void {
    this.filterValue = value;
    this.filteredOrganizations = this.getFilteredOrganizations();
    // после перерисовки списка подсветить первый найденный вариант, чтобы Enter выбирал его
    setTimeout(() => {
      if (this.organizationSelect?.panelOpen) {
        this.organizationSelect._keyManager?.setFirstItemActive();
      }
    });
  }

  // пропускаем к mat-select только клавиши навигации, остальные глушим,
  // чтобы набор текста не срабатывал как typeahead по опциям
  onSearchKeydown(event: KeyboardEvent): void {
    const navigationKeys = ['ArrowDown', 'ArrowUp', 'Enter', 'Escape'];
    if (!navigationKeys.includes(event.key)) {
      event.stopPropagation();
    } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      // штатная прокрутка mat-select не учитывает высоту поля поиска —
      // поправляем позицию после того, как key manager сдвинет подсветку
      setTimeout(() => this.keepActiveOptionVisible());
    }
  }

  onSelectOpenedChange(opened: boolean): void {
    if (opened) {
      this.searchInput?.nativeElement.focus();
      this.scrollSelectedOrganizationToCenter();
    } else {
      this.filterValue = '';
      this.filteredOrganizations = this.getFilteredOrganizations();
    }
  }

  private getFilteredOrganizations(): Organization[] {
    const filtered = this.organizations
      .filter(o => !this.filterValue || o.name && o.name.toLowerCase().includes(this.filterValue.toLowerCase()));
    return this.organizations.filter(o => filtered.some(f => o.id === f.id || o.id === f.parentId));
  }

  private get panelElement(): HTMLElement | null {
    return this.organizationSelect?.panel?.nativeElement ?? null;
  }

  // высота закреплённого поля поиска — на неё уменьшается видимая область опций
  private get searchFieldHeight(): number {
    return this.searchInput?.nativeElement.closest('.search-input')?.clientHeight ?? 0;
  }

  private keepActiveOptionVisible(): void {
    const panel = this.panelElement;
    const active = this.organizationSelect?._keyManager?.activeItem;
    if (!panel || !active) {
      return;
    }
    const optionRect = active._getHostElement().getBoundingClientRect();
    const panelRect = panel.getBoundingClientRect();
    const topLimit = panelRect.top + this.searchFieldHeight;
    if (optionRect.top < topLimit) {
      panel.scrollTop -= topLimit - optionRect.top;
    } else if (optionRect.bottom > panelRect.bottom) {
      panel.scrollTop += optionRect.bottom - panelRect.bottom;
    }
  }

  // прокручиваем панель так, чтобы выбранная организация была в середине
  // видимой области под закреплённым полем поиска
  private scrollSelectedOrganizationToCenter(): void {
    const panel = this.panelElement;
    const selected = this.organizationSelect?.selected;
    if (!panel || !selected || Array.isArray(selected)) {
      return;
    }
    const option = selected._getHostElement();
    const searchHeight = this.searchFieldHeight;
    const optionTop = option.getBoundingClientRect().top - panel.getBoundingClientRect().top + panel.scrollTop;
    const visibleHeight = panel.clientHeight - searchHeight;
    panel.scrollTop = optionTop - searchHeight - (visibleHeight - option.offsetHeight) / 2;
  }
}
